import { useContext, useEffect, useState } from "react";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import useAxios from "@hooks/use-axios";
import { UserContext } from "@context/user";
import { PUBLISHER_ENDPOINTS } from "@constants/endpoints";
import { kycSetupDefaults, kycSetupSchema } from "./schema";

const STEP_FIELDS = {
    1: ["full_name", "pan"],
    2: ["gst"],
    3: ["account_number", "account_number_confirm", "ifsc", "account_holder"]
};

const STEP_ENDPOINTS = {
    1: PUBLISHER_ENDPOINTS.KYC_PERSONAL,
    2: PUBLISHER_ENDPOINTS.KYC_TAX,
    3: PUBLISHER_ENDPOINTS.KYC_BANK
};

// The doc each step writes, so a per-doc `status` can be traced back to the
// step that has to be filled in again.
const STEP_DOCS = {
    1: "pan_card",
    2: "gst_number",
    3: "bank_account"
};

// A doc the reviewer turned down. The account-level `kyc_status` goes to
// "rejected" as soon as any one of them does, but only the doc says which.
const rejectedSteps = (docs = {}) => Object.entries(STEP_DOCS)
    .filter(([, key]) => docs[key]?.status === "rejected")
    .map(([step]) => Number(step));

const toFormValues = (docs = {}, holderName = "") => ({
    full_name             : holderName || docs.pan_card?.name || "",
    pan                   : docs.pan_card?.number || "",
    gst                   : docs.gst_number?.number || "",
    account_number        : docs.bank_account?.account_number || "",
    account_number_confirm: docs.bank_account?.account_number || "",
    ifsc                  : docs.bank_account?.ifsc_code || "",
    account_holder        : docs.bank_account?.account_holder_name || ""
});

const resumeStep = (docs = {}) => {
    // A rejection outranks anything missing — send them straight to the first
    // step that has to be corrected.
    const [firstRejected] = rejectedSteps(docs);
    if (firstRejected) return firstRejected;

    if (!docs.pan_card) return 1;
    if (!docs.gst_number) return 2;
    if (!docs.bank_account) return 3;
    return 3;
};

// What the wizard is busy with. `loading` off useAxios cannot say: it flips on
// for every request, so leaning on it would tear the form down mid-submit.
export const KYC_PHASE = {
    IDLE     : "idle",
    SAVING   : "saving",
    VERIFYING: "verifying"
};

const usePublisherKyc = (publisherId) => {
    const { axiosInstance } = useAxios({ base: "affiliate", initialLoader: true, showAlert: false });
    const { publishers } = useContext(UserContext);
    const holderName = publishers.find((publisher) => publisher.id === publisherId)?.holder_name || "";
    const [kyc, setKyc] = useState({});
    const [rejected, setRejected] = useState([]);
    const [step, setStep] = useState(1);
    const [done, setDone] = useState([]);
    const [phase, setPhase] = useState(KYC_PHASE.IDLE);
    // Only the first read blanks the page. Every later one happens behind the
    // wizard, which stays mounted so the step the user is on does not flicker.
    const [ready, setReady] = useState(false);

    const { control, watch, trigger, reset, setValue, formState: { errors, dirtyFields } } = useForm({
        resolver     : yupResolver(kycSetupSchema),
        defaultValues: kycSetupDefaults
    });
    const fullName = watch("full_name");
    const pan = watch("pan");
    const gst = watch("gst");
    const accountNumber = watch("account_number");
    const ifsc = watch("ifsc");
    const accountHolder = watch("account_holder");

    const fetchKyc = async () => {
        try {
            const res = await axiosInstance.get(PUBLISHER_ENDPOINTS.KYC_DETAILS(publisherId));
            const result = res.data || res;
            const data = result.data || result;
            const docs = data.kyc_docs || {};
            const resumeAt = resumeStep(docs);

            setKyc(data);
            setRejected(rejectedSteps(docs));
            setStep(resumeAt);
            setDone(Array.from({ length: resumeAt - 1 }, (_, i) => i + 1));
            reset(toFormValues(docs, holderName));
        } finally {
            // A 404 here just means nothing has been submitted yet — either way
            // the wizard takes over from the loader.
            setReady(true);
        }
    };

    useEffect(() => {
        fetchKyc();
    }, [publisherId]); // eslint-disable-line react-hooks/exhaustive-deps

    // The publisher list this name comes from is fetched by UserContext in
    // parallel, so it can land after the form was already reset. Fill it in when
    // it arrives, unless the user has since typed their own name over it.
    useEffect(() => {
        if (holderName && !dirtyFields.full_name) setValue("full_name", holderName);
    }, [holderName]); // eslint-disable-line react-hooks/exhaustive-deps

    const next = async () => {
        const isValid = await trigger(STEP_FIELDS[step]);
        if (!isValid) return;

        if (step === 2 && !gst) {
            setDone((prev) => (prev.includes(step) ? prev : [...prev, step]));
            setStep((prev) => prev + 1);
            return;
        }

        setPhase(KYC_PHASE.SAVING);
        try {
            const body = step === 1
                ? { full_name: fullName, pan_number: pan }
                : step === 2
                    ? { gst_number: gst }
                    : { account_number: accountNumber, ifsc_code: ifsc, account_holder_name: accountHolder };

            const res = await axiosInstance.post(STEP_ENDPOINTS[step](publisherId), body);
            const result = res.data || res;
            const docs = result.data || result;

            setKyc((prev) => ({ ...prev, kyc_docs: docs }));
            setRejected((prev) => prev.filter((s) => s !== step));
            setDone((prev) => (prev.includes(step) ? prev : [...prev, step]));

            if (step < 3) {
                setStep((prev) => prev + 1);
            } else {
                // Last step stored — hand the completed set to review before
                // re-reading, so the refetch already carries the new status.
                setPhase(KYC_PHASE.VERIFYING);
                await axiosInstance.post(PUBLISHER_ENDPOINTS.KYC_VERIFY(publisherId));
                await fetchKyc();
            }
        } finally {
            setPhase(KYC_PHASE.IDLE);
        }
    };

    const previous = () => setStep((prev) => Math.max(1, prev - 1));

    return {
        kyc,
        // Steps whose doc came back rejected. Non-empty means the wizard has to
        // stay open on an otherwise "submitted" KYC so they can resubmit.
        rejected,
        ready,
        phase,
        control,
        errors,
        step,
        done,
        next,
        previous,
        submitting: phase !== KYC_PHASE.IDLE,
        fullName,
        pan,
        gst,
        accountNumber,
        ifsc,
        accountHolder
    };
};

export default usePublisherKyc;
